import { useMemo, useState } from "react";
import { weatherSnapshot, workoutSnapshot } from "./data/garageScenario.mock";
import { buildRecommendation } from "./services/recommendationService";
import { mapToGarageAthleteProfile } from "./services/athleteProfileAdapter";
import { createEquipmentId, createEquipmentRegistryRepository, type EquipmentItem } from "./services/equipmentRegistryRepository";
import { Card } from "../../components/ui/Card";
import { SectionHeader } from "../../components/ui/SectionHeader";
import { StatusBadge } from "../../components/ui/StatusBadge";
import { useAthleteProfile } from "../athlete-profile/AthleteProfileProvider";
import type {
  BikeEquipment,
  DayTiming,
  EquipmentCategory,
  EquipmentLifecycleStatus,
  EquipmentReadinessStatus,
  EquipmentRegistry,
  GymEquipment,
  RecoveryEquipment,
  RecoveryEquipmentType,
  SensorEquipment,
  SensorType
} from "./types/garage";

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export function GaragePage() {
  const { athleteProfile } = useAthleteProfile();
  const repository = useMemo(() => createEquipmentRegistryRepository(), []);
  const [registry, setRegistry] = useState<EquipmentRegistry>(() => repository.loadRegistry());
  const [categoryFilter, setCategoryFilter] = useState<"all" | EquipmentCategory>("all");
  const [statusFilter, setStatusFilter] = useState<"all" | EquipmentLifecycleStatus | EquipmentReadinessStatus>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [editorState, setEditorState] = useState<{ mode: "add" | "edit"; item: EquipmentItem } | null>(null);
  const [deleteState, setDeleteState] = useState<{ category: EquipmentCategory; id: string; name: string; warnings: string[] } | null>(null);
  const garageAthleteProfile = mapToGarageAthleteProfile(athleteProfile);

  const recommendation = buildRecommendation({
    athlete: garageAthleteProfile,
    weather: weatherSnapshot,
    workout: workoutSnapshot,
    bikes: registry.bikes,
    recoveryEquipment: registry.recoveryEquipment
  });

  const maintenanceDue = registry.bikes.filter(bike => bike.readinessStatus === "maintenance-due" && !bike.archived);

  function persist(next: EquipmentRegistry) {
    setRegistry(next);
  }

  function openAddEditor(category: EquipmentCategory) {
    setEditorState({
      mode: "add",
      item: createDefaultItem(category)
    });
  }

  function saveEditorItem() {
    if (!editorState) {
      return;
    }

    const next = editorState.mode === "add"
      ? repository.addEquipment(editorState.item)
      : repository.updateEquipment(editorState.item);
    persist(next);
    setEditorState(null);
  }

  function toggleArchive(item: EquipmentItem) {
    const next = item.archived
      ? repository.restoreEquipment(item.category, item.id)
      : repository.archiveEquipment(item.category, item.id);
    persist(next);
  }

  function requestDelete(item: EquipmentItem) {
    const warnings = repository.getDeletionWarnings(registry, item.category, item.id);
    setDeleteState({ category: item.category, id: item.id, name: equipmentDisplayName(item), warnings });
  }

  function confirmDelete() {
    if (!deleteState) {
      return;
    }

    const result = repository.deleteEquipment(deleteState.category, deleteState.id, { confirmed: true });
    if (result.deleted) {
      persist(result.registry);
    }
    setDeleteState(null);
  }

  const normalizedSearch = searchTerm.trim().toLowerCase();

  function matchesFilters(item: EquipmentItem): boolean {
    if (categoryFilter !== "all" && item.category !== categoryFilter) {
      return false;
    }

    if (statusFilter !== "all" && item.lifecycleStatus !== statusFilter && item.readinessStatus !== statusFilter) {
      return false;
    }

    if (!normalizedSearch) {
      return true;
    }

    const haystack = [
      equipmentDisplayName(item),
      item.notes,
      item.lifecycleStatus,
      item.readinessStatus,
      item.category,
      item.category === "bike" ? `${item.manufacturer} ${item.model}` : "",
      item.category === "sensor" ? item.firmware : ""
    ].join(" ").toLowerCase();

    return haystack.includes(normalizedSearch);
  }

  const visibleBikes = registry.bikes.filter(matchesFilters);
  const visibleRecovery = registry.recoveryEquipment.filter(matchesFilters);
  const visibleSensors = registry.sensors.filter(matchesFilters);
  const visibleGym = registry.gymEquipment.filter(matchesFilters);

  return (
    <div className="garage-layout">
      <Card>
        <SectionHeader
          eyebrow="EQUIPMENT"
          title="Equipment Registry"
          subtitle="Editable local-first registry with archive-first safety"
          action={<button type="button" className="ride-analysis-button primary-action" onClick={() => openAddEditor("bike")}>Add Equipment</button>}
        />
        <div className="garage-toolbar">
          <label>
            Category
            <select value={categoryFilter} onChange={event => setCategoryFilter(event.target.value as "all" | EquipmentCategory)}>
              <option value="all">All categories</option>
              <option value="bike">Bikes</option>
              <option value="recovery">Recovery Equipment</option>
              <option value="sensor">Sensors and Technology</option>
              <option value="gym">Gym Equipment</option>
            </select>
          </label>
          <label>
            Status
            <select value={statusFilter} onChange={event => setStatusFilter(event.target.value as "all" | EquipmentLifecycleStatus | EquipmentReadinessStatus)}>
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="planned">Planned</option>
              <option value="retired">Retired</option>
              <option value="sold">Sold</option>
              <option value="ready">Ready</option>
              <option value="maintenance-due">Maintenance due</option>
              <option value="unavailable">Unavailable</option>
            </select>
          </label>
          <label className="garage-search-field">
            Search
            <input
              value={searchTerm}
              onChange={event => setSearchTerm(event.target.value)}
              placeholder="Search by name, notes, model..."
            />
          </label>
          <label>
            Quick add
            <select onChange={event => {
              const value = event.target.value as EquipmentCategory | "";
              if (value) {
                openAddEditor(value);
                event.target.value = "";
              }
            }} defaultValue="">
              <option value="" disabled>Select category</option>
              <option value="bike">Bike</option>
              <option value="recovery">Recovery Equipment</option>
              <option value="sensor">Sensor and Technology</option>
              <option value="gym">Gym Equipment</option>
            </select>
          </label>
        </div>
      </Card>

      <Card>
        <SectionHeader
          eyebrow="BIKES"
          title="Bike Registry"
          subtitle={`${visibleBikes.length} shown of ${registry.bikes.length}`}
        />
        {visibleBikes.length === 0 ? <GarageEmptyState message="No bikes match your filters." /> : (
          <div className="garage-grid">
            {visibleBikes.map(bike => (
            <article key={bike.id} className="garage-item">
              <div className="garage-item-header">
                <h3>{bike.year} {bike.manufacturer} {bike.model}</h3>
                <StatusBadge label={bike.readinessStatus === "ready" ? "Ready" : bike.readinessStatus === "maintenance-due" ? "Maintenance Due" : "Unavailable"} tone={bike.readinessStatus === "ready" ? "positive" : "attention"} />
              </div>
              <p>{bike.nickname} | Profiles: {bike.rideProfile.join(", ")}</p>
              <p>Status: {bike.lifecycleStatus}</p>
              <p>Drivetrain: {bike.drivetrain.groupset}</p>
              <p>Wheels: {bike.wheels.wheelset} | {bike.wheels.tireModel} {bike.wheels.tireWidthMm}mm</p>
              <ItemActions item={bike} onEdit={setEditorState} onArchive={toggleArchive} onDelete={requestDelete} />
            </article>
            ))}
          </div>
        )}
      </Card>

      <Card>
        <SectionHeader
          eyebrow="RECOVERY EQUIPMENT"
          title="Recovery Registry"
          subtitle={`${visibleRecovery.length} shown of ${registry.recoveryEquipment.length}`}
        />
        {visibleRecovery.length === 0 ? <GarageEmptyState message="No recovery equipment matches your filters." /> : (
          <div className="garage-grid">
            {visibleRecovery.map(item => (
            <article key={item.id} className="garage-item">
              <div className="garage-item-header">
                <h3>{item.name}</h3>
                <StatusBadge label={item.readinessStatus === "ready" ? "Ready" : item.readinessStatus === "planned" ? "Planned" : "Unavailable"} tone={item.readinessStatus === "ready" ? "positive" : "neutral"} />
              </div>
              <p>Temperature: {item.defaultTemperatureF === null ? "--" : `${item.defaultTemperatureF}F`}</p>
              <p>Duration: {item.defaultDurationMinutes === null ? "--" : `${item.defaultDurationMinutes} minutes`}</p>
              <p>Preferred time: {item.preferredTiming ?? "--"}</p>
              <p>Contrast protocols: {item.contrastTherapyProtocols.length}</p>
              <ItemActions item={item} onEdit={setEditorState} onArchive={toggleArchive} onDelete={requestDelete} />
            </article>
            ))}
          </div>
        )}
      </Card>

      <Card>
        <SectionHeader
          eyebrow="SENSORS"
          title="Sensor Registry"
          subtitle={`${visibleSensors.length} shown of ${registry.sensors.length}`}
        />
        {visibleSensors.length === 0 ? <GarageEmptyState message="No sensors match your filters." /> : (
          <div className="garage-grid">
            {visibleSensors.map(sensor => (
            <article key={sensor.id} className="garage-item">
              <div className="garage-item-header">
                <h3>{sensor.name}</h3>
                <StatusBadge label={sensor.readinessStatus === "ready" ? "Ready" : "Unavailable"} tone={sensor.readinessStatus === "ready" ? "positive" : "attention"} />
              </div>
              <p>Battery: {sensor.batteryPercent === null ? "--" : `${sensor.batteryPercent}%`}</p>
              <p>Firmware: {sensor.firmware}</p>
              <p>Last sync: {sensor.lastSyncIso ? formatDate(sensor.lastSyncIso) : "--"}</p>
              <p>Assigned bike: {registry.bikes.find(bike => bike.id === sensor.assignedBikeId)?.nickname ?? "Unassigned"}</p>
              <ItemActions item={sensor} onEdit={setEditorState} onArchive={toggleArchive} onDelete={requestDelete} />
            </article>
            ))}
          </div>
        )}
      </Card>

      <Card>
        <SectionHeader
          eyebrow="GYM EQUIPMENT"
          title="Gym Registry"
          subtitle={`${visibleGym.length} shown of ${registry.gymEquipment.length}`}
        />
        {visibleGym.length === 0 ? <GarageEmptyState message="No gym equipment matches your filters." /> : (
          <div className="garage-grid">
            {visibleGym.map(item => (
              <article key={item.id} className="garage-item">
                <div className="garage-item-header">
                  <h3>{item.name}</h3>
                  <StatusBadge label={item.lifecycleStatus} tone={item.lifecycleStatus === "active" ? "positive" : "neutral"} />
                </div>
                <p>Category: {item.gymCategory}</p>
                <p>Quantity: {item.quantity}</p>
                <ItemActions item={item} onEdit={setEditorState} onArchive={toggleArchive} onDelete={requestDelete} />
              </article>
            ))}
          </div>
        )}
      </Card>

      <Card>
        <SectionHeader
          eyebrow="MAINTENANCE STATUS"
          title="Service Watch"
          subtitle={maintenanceDue.length === 0 ? "All bikes currently ready" : `${maintenanceDue.length} bike(s) need attention`}
        />
        <div className="garage-grid">
          {registry.bikes.map(bike => (
            <article key={`${bike.id}-maintenance`} className="garage-item">
              <h3>{bike.nickname}</h3>
              <p>Chain wax: {bike.maintenance.chainWaxDate ? formatDate(bike.maintenance.chainWaxDate) : "--"}</p>
              <p>Chain mileage: {bike.maintenance.chainMileageMiles} miles</p>
              <p>Total bike mileage: {bike.maintenance.totalMileageMiles} miles</p>
              <p>Brake pads: {bike.maintenance.brakePads}</p>
              <p>Rotor status: {bike.maintenance.rotors}</p>
              <p>Di2 battery: {bike.maintenance.di2BatteryPercent === null ? "--" : `${bike.maintenance.di2BatteryPercent}%`}</p>
              <p>Sealant: {bike.maintenance.tireSealantDate ? formatDate(bike.maintenance.tireSealantDate) : "--"}</p>
              <p>Last service: {bike.maintenance.lastServiceDate ? formatDate(bike.maintenance.lastServiceDate) : "--"}</p>
            </article>
          ))}
        </div>
      </Card>

      <Card>
        <SectionHeader
          eyebrow="RECOMMENDATIONS"
          title="Rule-Based Daily Recommendation"
          subtitle={`${workoutSnapshot.title} | ${weatherSnapshot.condition}`}
          action={<StatusBadge label={`Confidence ${Math.round(recommendation.confidence * 100)}%`} tone="positive" />}
        />
        <div className="garage-recommendation-grid">
          <article className="garage-item">
            <h3>Recommended Bike</h3>
            <p>{recommendation.recommendedBike ? `${recommendation.recommendedBike.year} ${recommendation.recommendedBike.manufacturer} ${recommendation.recommendedBike.model}` : "No bike match"}</p>
          </article>
          <article className="garage-item">
            <h3>Recovery Protocol</h3>
            <p>{recommendation.recoveryProtocol.modality}</p>
            <p>{recommendation.recoveryProtocol.target}</p>
            <p>{recommendation.recoveryProtocol.timing}</p>
          </article>
          <article className="garage-item">
            <h3>Nutrition Protocol</h3>
            <p>Carbs: {recommendation.nutritionProtocol.carbsPerHour}</p>
            <p>Hydration: {recommendation.nutritionProtocol.hydrationPerHour}</p>
            <p>Sodium: {recommendation.nutritionProtocol.sodiumPerHour}</p>
          </article>
        </div>
        <ul className="command-list compact-list">
          {recommendation.reasoning.map(reason => (
            <li key={reason}>{reason}</li>
          ))}
        </ul>
      </Card>

      {editorState && (
        <div className="garage-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="equipment-editor-title">
          <div className="garage-modal">
            <header className="garage-modal-header">
              <h2 id="equipment-editor-title">{editorState.mode === "add" ? "Add Equipment" : "Edit Equipment"}</h2>
              <button type="button" className="ride-analysis-button" onClick={() => setEditorState(null)}>Cancel</button>
            </header>
            <div className="garage-modal-body">
              <EditorCategorySelector
                category={editorState.item.category}
                onCategoryChange={category => setEditorState({ mode: editorState.mode, item: createDefaultItem(category) })}
                disableCategoryChange={editorState.mode === "edit"}
              />
              <BaseFieldsEditor
                item={editorState.item}
                onChange={item => setEditorState({ ...editorState, item })}
              />
              {editorState.item.category === "bike" && (
                <BikeEditor
                  bike={editorState.item}
                  onChange={bike => setEditorState({ ...editorState, item: bike })}
                />
              )}
              {editorState.item.category === "recovery" && (
                <RecoveryEditor
                  item={editorState.item}
                  onChange={item => setEditorState({ ...editorState, item })}
                />
              )}
              {editorState.item.category === "sensor" && (
                <SensorEditor
                  item={editorState.item}
                  bikes={registry.bikes}
                  onChange={item => setEditorState({ ...editorState, item })}
                />
              )}
              {editorState.item.category === "gym" && (
                <GymEditor
                  item={editorState.item}
                  onChange={item => setEditorState({ ...editorState, item })}
                />
              )}
            </div>
            <footer className="garage-modal-footer">
              <button type="button" className="ride-analysis-button" onClick={() => setEditorState(null)}>Cancel</button>
              <button type="button" className="ride-analysis-button garage-save-button primary-action" onClick={saveEditorItem}>Save</button>
            </footer>
          </div>
        </div>
      )}

      {deleteState && (
        <div className="garage-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="equipment-delete-title">
          <div className="garage-modal garage-confirm-modal">
            <header className="garage-modal-header">
              <h2 id="equipment-delete-title">Confirm Deletion</h2>
            </header>
            <div className="garage-modal-body">
              <p>Delete {deleteState.name}? This action cannot be undone.</p>
              {deleteState.warnings.length > 0 && (
                <ul className="command-list compact-list">
                  {deleteState.warnings.map(warning => <li key={warning}>{warning}</li>)}
                </ul>
              )}
              {deleteState.warnings.length === 0 && (
                <p>No active references detected.</p>
              )}
            </div>
            <footer className="garage-modal-footer">
              <button type="button" className="ride-analysis-button" onClick={() => setDeleteState(null)}>Cancel</button>
              <button type="button" className="ride-analysis-button garage-delete-button danger-action" onClick={confirmDelete}>Delete</button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

function GarageEmptyState({ message }: { message: string }) {
  return (
    <div className="garage-empty-state">
      <p>{message}</p>
    </div>
  );
}

function ItemActions({
  item,
  onEdit,
  onArchive,
  onDelete
}: {
  item: EquipmentItem;
  onEdit: (value: { mode: "add" | "edit"; item: EquipmentItem } | null) => void;
  onArchive: (item: EquipmentItem) => void;
  onDelete: (item: EquipmentItem) => void;
}) {
  return (
    <div className="garage-item-actions">
      <button type="button" className="ride-analysis-button" onClick={() => onEdit({ mode: "edit", item })}>Edit</button>
      <button type="button" className="ride-analysis-button" onClick={() => onArchive(item)}>{item.archived ? "Restore" : "Archive"}</button>
      <button type="button" className="ride-analysis-button danger-action" onClick={() => onDelete(item)}>Delete</button>
    </div>
  );
}

function EditorCategorySelector({
  category,
  onCategoryChange,
  disableCategoryChange
}: {
  category: EquipmentCategory;
  onCategoryChange: (category: EquipmentCategory) => void;
  disableCategoryChange: boolean;
}) {
  return (
    <div className="garage-editor-grid">
      <label>
        Category
        <select
          value={category}
          onChange={event => onCategoryChange(event.target.value as EquipmentCategory)}
          disabled={disableCategoryChange}
        >
          <option value="bike">Bike</option>
          <option value="recovery">Recovery Equipment</option>
          <option value="sensor">Sensor and Technology</option>
          <option value="gym">Gym Equipment</option>
        </select>
      </label>
    </div>
  );
}

function BaseFieldsEditor({ item, onChange }: { item: EquipmentItem; onChange: (item: EquipmentItem) => void }) {
  return (
    <section className="garage-form-section">
      <h3>Status and Notes</h3>
      <div className="garage-editor-grid">
        <label>
          Lifecycle status
          <select
            value={item.lifecycleStatus}
            onChange={event => onChange({ ...item, lifecycleStatus: event.target.value as EquipmentLifecycleStatus })}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="planned">Planned</option>
            <option value="retired">Retired</option>
            <option value="sold">Sold</option>
          </select>
        </label>
        <label>
          Readiness
          <select
            value={item.readinessStatus}
            onChange={event => onChange({ ...item, readinessStatus: event.target.value as EquipmentReadinessStatus })}
          >
            <option value="ready">Ready</option>
            <option value="maintenance-due">Maintenance due</option>
            <option value="unavailable">Unavailable</option>
            <option value="planned">Planned</option>
          </select>
        </label>
        <label className="garage-editor-span-2">
          Notes
          <textarea value={item.notes} onChange={event => onChange({ ...item, notes: event.target.value })} rows={3} />
        </label>
      </div>
    </section>
  );
}

function BikeEditor({ bike, onChange }: { bike: BikeEquipment; onChange: (bike: BikeEquipment) => void }) {
  const profileOptions: BikeEquipment["rideProfile"] = ["road", "gravel", "trainer", "race"];

  return (
    <>
      <section className="garage-form-section">
        <h3>General</h3>
        <div className="garage-editor-grid">
          <label>
            Manufacturer
            <input value={bike.manufacturer} onChange={event => onChange({ ...bike, manufacturer: event.target.value })} />
          </label>
          <label>
            Model
            <input value={bike.model} onChange={event => onChange({ ...bike, model: event.target.value })} />
          </label>
          <label>
            Year
            <input type="number" value={bike.year} onChange={event => onChange({ ...bike, year: Number(event.target.value || bike.year) })} />
          </label>
          <label>
            Nickname
            <input value={bike.nickname} onChange={event => onChange({ ...bike, nickname: event.target.value })} />
          </label>
          <label>
            Purchase date
            <input type="date" value={bike.purchaseDate ?? ""} onChange={event => onChange({ ...bike, purchaseDate: event.target.value || null })} />
          </label>
          <div className="garage-editor-span-2 garage-checkbox-grid">
            {profileOptions.map(profile => (
              <label key={profile}>
                <input
                  type="checkbox"
                  checked={bike.rideProfile.includes(profile)}
                  onChange={event => {
                    const next = event.target.checked
                      ? [...bike.rideProfile, profile]
                      : bike.rideProfile.filter(value => value !== profile);
                    onChange({ ...bike, rideProfile: next.length === 0 ? ["road"] : next });
                  }}
                />
                {profile}
              </label>
            ))}
          </div>
        </div>
      </section>

      <section className="garage-form-section">
        <h3>Drivetrain</h3>
        <div className="garage-editor-grid">
          <label>
            Groupset
            <input value={bike.drivetrain.groupset} onChange={event => onChange({ ...bike, drivetrain: { ...bike.drivetrain, groupset: event.target.value } })} />
          </label>
          <label>
            Cassette
            <input value={bike.drivetrain.cassette} onChange={event => onChange({ ...bike, drivetrain: { ...bike.drivetrain, cassette: event.target.value } })} />
          </label>
          <label>
            Chainrings
            <input value={bike.drivetrain.chainrings} onChange={event => onChange({ ...bike, drivetrain: { ...bike.drivetrain, chainrings: event.target.value } })} />
          </label>
          <label>
            Chain
            <input value={bike.drivetrain.chain} onChange={event => onChange({ ...bike, drivetrain: { ...bike.drivetrain, chain: event.target.value } })} />
          </label>
          <label>
            Bottom bracket
            <input value={bike.drivetrain.bottomBracket} onChange={event => onChange({ ...bike, drivetrain: { ...bike.drivetrain, bottomBracket: event.target.value } })} />
          </label>
          <label>
            Crank length (mm)
            <input type="number" value={bike.drivetrain.crankLengthMm ?? ""} onChange={event => onChange({ ...bike, drivetrain: { ...bike.drivetrain, crankLengthMm: event.target.value ? Number(event.target.value) : null } })} />
          </label>
        </div>
      </section>

      <section className="garage-form-section">
        <h3>Wheels and Tires</h3>
        <div className="garage-editor-grid">
          <label>
            Wheelset
            <input value={bike.wheels.wheelset} onChange={event => onChange({ ...bike, wheels: { ...bike.wheels, wheelset: event.target.value } })} />
          </label>
          <label>
            Tire model
            <input value={bike.wheels.tireModel} onChange={event => onChange({ ...bike, wheels: { ...bike.wheels, tireModel: event.target.value } })} />
          </label>
          <label>
            Tire width (mm)
            <input type="number" value={bike.wheels.tireWidthMm} onChange={event => onChange({ ...bike, wheels: { ...bike.wheels, tireWidthMm: Number(event.target.value || bike.wheels.tireWidthMm) } })} />
          </label>
          <label>
            Front pressure (psi)
            <input type="number" value={bike.wheels.tirePressureFrontPsi ?? ""} onChange={event => onChange({ ...bike, wheels: { ...bike.wheels, tirePressureFrontPsi: event.target.value ? Number(event.target.value) : null } })} />
          </label>
          <label>
            Rear pressure (psi)
            <input type="number" value={bike.wheels.tirePressureRearPsi ?? ""} onChange={event => onChange({ ...bike, wheels: { ...bike.wheels, tirePressureRearPsi: event.target.value ? Number(event.target.value) : null } })} />
          </label>
          <label>
            Tubeless
            <select value={bike.wheels.tubeless ? "yes" : "no"} onChange={event => onChange({ ...bike, wheels: { ...bike.wheels, tubeless: event.target.value === "yes" } })}>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </label>
          <label>
            Sealant date
            <input type="date" value={bike.maintenance.tireSealantDate ?? ""} onChange={event => onChange({ ...bike, maintenance: { ...bike.maintenance, tireSealantDate: event.target.value || null } })} />
          </label>
        </div>
      </section>

      <section className="garage-form-section">
        <h3>Maintenance</h3>
        <div className="garage-editor-grid">
          <label>
            Last chain wax date
            <input type="date" value={bike.maintenance.chainWaxDate ?? ""} onChange={event => onChange({ ...bike, maintenance: { ...bike.maintenance, chainWaxDate: event.target.value || null } })} />
          </label>
          <label>
            Chain mileage since wax
            <input type="number" value={bike.maintenance.chainMileageMiles} onChange={event => onChange({ ...bike, maintenance: { ...bike.maintenance, chainMileageMiles: Number(event.target.value || 0) } })} />
          </label>
          <label>
            Total bike mileage
            <input type="number" value={bike.maintenance.totalMileageMiles} onChange={event => onChange({ ...bike, maintenance: { ...bike.maintenance, totalMileageMiles: Number(event.target.value || 0) } })} />
          </label>
          <label>
            Brake pad status
            <select value={bike.maintenance.brakePads} onChange={event => onChange({ ...bike, maintenance: { ...bike.maintenance, brakePads: event.target.value as BikeEquipment["maintenance"]["brakePads"] } })}>
              <option value="good">Good</option>
              <option value="watch">Watch</option>
              <option value="replace">Replace</option>
            </select>
          </label>
          <label>
            Rotor status
            <select value={bike.maintenance.rotors} onChange={event => onChange({ ...bike, maintenance: { ...bike.maintenance, rotors: event.target.value as BikeEquipment["maintenance"]["rotors"] } })}>
              <option value="good">Good</option>
              <option value="watch">Watch</option>
              <option value="replace">Replace</option>
            </select>
          </label>
          <label>
            Di2 battery (%)
            <input type="number" value={bike.maintenance.di2BatteryPercent ?? ""} onChange={event => onChange({ ...bike, maintenance: { ...bike.maintenance, di2BatteryPercent: event.target.value ? Number(event.target.value) : null } })} />
          </label>
          <label>
            Last service date
            <input type="date" value={bike.maintenance.lastServiceDate ?? ""} onChange={event => onChange({ ...bike, maintenance: { ...bike.maintenance, lastServiceDate: event.target.value || null } })} />
          </label>
          <label className="garage-editor-span-2">
            Next service note
            <input value={bike.maintenance.nextServiceNote} onChange={event => onChange({ ...bike, maintenance: { ...bike.maintenance, nextServiceNote: event.target.value } })} />
          </label>
        </div>
      </section>
    </>
  );
}

function RecoveryEditor({ item, onChange }: { item: RecoveryEquipment; onChange: (item: RecoveryEquipment) => void }) {
  return (
    <section className="garage-form-section">
      <h3>Recovery Defaults</h3>
      <div className="garage-editor-grid">
        <label>
          Name
          <input value={item.name} onChange={event => onChange({ ...item, name: event.target.value })} />
        </label>
        <label>
          Type
          <select value={item.type} onChange={event => onChange({ ...item, type: event.target.value as RecoveryEquipmentType })}>
            <option value="cold-plunge">Cold plunge</option>
            <option value="hot-tub">Hot tub</option>
            <option value="sauna">Sauna</option>
            <option value="massage-gun">Massage gun</option>
          </select>
        </label>
        <label>
          Preferred timing
          <select value={item.preferredTiming ?? ""} onChange={event => onChange({ ...item, preferredTiming: (event.target.value || null) as DayTiming | null })}>
            <option value="">None</option>
            <option value="immediately-after-waking">Immediately after waking</option>
            <option value="morning">Morning</option>
            <option value="afternoon">Afternoon</option>
            <option value="evening">Evening</option>
          </select>
        </label>
        <label>
          Default temperature (F)
          <input type="number" value={item.defaultTemperatureF ?? ""} onChange={event => onChange({ ...item, defaultTemperatureF: event.target.value ? Number(event.target.value) : null })} />
        </label>
        <label>
          Default duration (minutes)
          <input type="number" value={item.defaultDurationMinutes ?? ""} onChange={event => onChange({ ...item, defaultDurationMinutes: event.target.value ? Number(event.target.value) : null })} />
        </label>
      </div>
      <div className="garage-form-section-subtle">
        <h4>Optional Contrast Protocols</h4>
        {item.contrastTherapyProtocols.length === 0 && <p>No optional contrast protocols configured.</p>}
        {item.contrastTherapyProtocols.map(protocol => (
          <div key={protocol.id} className="garage-contrast-item">
            <label>
              Enabled
              <select value={protocol.enabled ? "yes" : "no"} onChange={event => onChange({
                ...item,
                contrastTherapyProtocols: item.contrastTherapyProtocols.map(current => current.id === protocol.id ? { ...current, enabled: event.target.value === "yes" } : current)
              })}>
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
            </label>
            <label>
              Rounds
              <input type="number" value={protocol.rounds} onChange={event => onChange({
                ...item,
                contrastTherapyProtocols: item.contrastTherapyProtocols.map(current => current.id === protocol.id ? { ...current, rounds: Number(event.target.value || 1) } : current)
              })} />
            </label>
            <label>
              Cold minutes
              <input type="number" value={protocol.coldDurationMinutes} onChange={event => onChange({
                ...item,
                contrastTherapyProtocols: item.contrastTherapyProtocols.map(current => current.id === protocol.id ? { ...current, coldDurationMinutes: Number(event.target.value || 1) } : current)
              })} />
            </label>
            <label>
              Hot minutes
              <input type="number" value={protocol.hotDurationMinutes} onChange={event => onChange({
                ...item,
                contrastTherapyProtocols: item.contrastTherapyProtocols.map(current => current.id === protocol.id ? { ...current, hotDurationMinutes: Number(event.target.value || 1) } : current)
              })} />
            </label>
          </div>
        ))}
      </div>
    </section>
  );
}

function SensorEditor({
  item,
  bikes,
  onChange
}: {
  item: SensorEquipment;
  bikes: BikeEquipment[];
  onChange: (item: SensorEquipment) => void;
}) {
  return (
    <section className="garage-form-section">
      <h3>Sensor Details</h3>
      <div className="garage-editor-grid">
        <label>
          Name
          <input value={item.name} onChange={event => onChange({ ...item, name: event.target.value })} />
        </label>
        <label>
          Type
          <select value={item.type} onChange={event => onChange({ ...item, type: event.target.value as SensorType })}>
            <option value="bike-computer">Bike computer</option>
            <option value="watch">Watch</option>
            <option value="core-temp">Core temp</option>
            <option value="respiratory">Respiratory</option>
            <option value="power-meter">Power meter</option>
            <option value="trainer">Trainer</option>
          </select>
        </label>
        <label>
          Battery (%)
          <input type="number" value={item.batteryPercent ?? ""} onChange={event => onChange({ ...item, batteryPercent: event.target.value ? Number(event.target.value) : null })} />
        </label>
        <label>
          Firmware
          <input value={item.firmware} onChange={event => onChange({ ...item, firmware: event.target.value })} />
        </label>
        <label>
          Last sync
          <input type="date" value={item.lastSyncIso ? item.lastSyncIso.slice(0, 10) : ""} onChange={event => onChange({ ...item, lastSyncIso: event.target.value ? `${event.target.value}T00:00:00.000Z` : null })} />
        </label>
        <label>
          Assigned bike
          <select value={item.assignedBikeId ?? ""} onChange={event => onChange({ ...item, assignedBikeId: event.target.value || null })}>
            <option value="">Unassigned</option>
            {bikes.map(bike => (
              <option key={bike.id} value={bike.id}>{bike.nickname}</option>
            ))}
          </select>
        </label>
      </div>
    </section>
  );
}

function GymEditor({ item, onChange }: { item: GymEquipment; onChange: (item: GymEquipment) => void }) {
  return (
    <section className="garage-form-section">
      <h3>Gym Details</h3>
      <div className="garage-editor-grid">
        <label>
          Name
          <input value={item.name} onChange={event => onChange({ ...item, name: event.target.value })} />
        </label>
        <label>
          Category
          <select value={item.gymCategory} onChange={event => onChange({ ...item, gymCategory: event.target.value as GymEquipment["gymCategory"] })}>
            <option value="strength">Strength</option>
            <option value="conditioning">Conditioning</option>
            <option value="mobility">Mobility</option>
            <option value="recovery">Recovery</option>
          </select>
        </label>
        <label>
          Quantity
          <input type="number" min={0} value={item.quantity} onChange={event => onChange({ ...item, quantity: Number(event.target.value || 0) })} />
        </label>
      </div>
    </section>
  );
}

function equipmentDisplayName(item: EquipmentItem): string {
  if (item.category === "bike") {
    return `${item.year} ${item.manufacturer} ${item.model}`;
  }

  return item.name;
}

function createDefaultItem(category: EquipmentCategory): EquipmentItem {
  const timestamp = new Date().toISOString();
  const base = {
    id: createEquipmentId(category),
    category,
    lifecycleStatus: "active" as EquipmentLifecycleStatus,
    readinessStatus: "ready" as EquipmentReadinessStatus,
    archived: false,
    notes: "",
    createdAtIso: timestamp,
    updatedAtIso: timestamp
  };

  if (category === "bike") {
    return {
      ...base,
      category: "bike",
      nickname: "",
      manufacturer: "",
      model: "",
      year: new Date().getFullYear(),
      purchaseDate: null,
      rideProfile: ["road"],
      drivetrain: {
        groupset: "",
        cassette: "",
        chainrings: "",
        chain: "",
        bottomBracket: "",
        crankLengthMm: null
      },
      wheels: {
        wheelset: "",
        tireModel: "",
        tireWidthMm: 28,
        tubeless: true,
        tirePressureFrontPsi: null,
        tirePressureRearPsi: null
      },
      maintenance: {
        chainWaxDate: null,
        chainMileageMiles: 0,
        totalMileageMiles: 0,
        brakePads: "good",
        rotors: "good",
        di2BatteryPercent: null,
        tireSealantDate: null,
        lastServiceDate: null,
        nextServiceNote: ""
      }
    };
  }

  if (category === "recovery") {
    return {
      ...base,
      category: "recovery",
      name: "Cold Plunge",
      type: "cold-plunge",
      preferredTiming: "immediately-after-waking",
      defaultTemperatureF: 44,
      defaultDurationMinutes: 4,
      contrastTherapyProtocols: []
    };
  }

  if (category === "sensor") {
    return {
      ...base,
      category: "sensor",
      name: "New Sensor",
      type: "watch",
      batteryPercent: null,
      firmware: "",
      lastSyncIso: null,
      assignedBikeId: null
    };
  }

  return {
    ...base,
    category: "gym",
    name: "New Gym Item",
    gymCategory: "strength",
    quantity: 1
  };
}
